import _extends from '@babel/runtime-corejs3/helpers/extends';
import _slicedToArray from '@babel/runtime-corejs3/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime-corejs3/helpers/objectWithoutProperties';
import _parseFloat from '@babel/runtime-corejs3/core-js-stable/parse-float';
import _fillInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/fill';
import React__default, { useRef } from 'react';
import classNames from 'classnames';
import styles from './06e82d99deafe3745882c79d5544d22b6e46613c55f2fecd2f6394a180146c1f.cfe-internal.js';
import SliderButton from './0630c3b56e56d705358bf146a6bc62571de17649b682127bb048c5ea16f4dd46.cfe-internal.js';
import '../../hooks/8930fff059088aae845742b95b03a9c78f1349f3c58d1781f854d37c28181edd.cfe-internal.js';
import useControllableState from '../../hooks/useControllableState/fc7c2512fc1249f4837f2cb1f271d18191fa6aebd32c5f03023614c2b76711ef.cfe-internal.js';
import useSSRLayoutEffect from '../../hooks/useSSRLayoutEffect/1b5a8c3b5463c14b01e195c9e403e1d1bbb3a66e0ef365e78fe012b249d635d1.cfe-internal.js';

var _excluded = ["className", "defaultValue", "formatter", "max", "min", "onChange", "step", "value", "aria-label", "aria-labelledby", "aria-describedby"];
var clamp = function clamp(value, max, min) {
  return Math.min(Math.max(value, min), max);
};
var countDecimals = function countDecimals(value) {
  if (value % 1 !== 0) {
    return value.toString().split('.')[1].length;
  }
  return 0;
};
var roundToStep = function roundToStep(value, max, min, step) {
  var newValue = value;
  var mod = (value - min) % step;
  newValue += mod > step / 2 ? step - mod : -mod;
  if (value >= max) {
    newValue = max;
  }
  if (value <= min) {
    newValue = min;
  }
  newValue = clamp(newValue, max, min);
  return _parseFloat(newValue.toFixed(countDecimals(step)));
};
var valueToPercent = function valueToPercent(value, max, min) {
  return (value - min) * 100 / (max - min);
};
var pointToValue = function pointToValue(point, max, min, width) {
  return point * (max - min) / width + min;
};
var Slider = /*#__PURE__*/React__default.forwardRef(function (_ref, ref) {
  var className = _ref.className,
    defaultValue = _ref.defaultValue,
    formatter = _ref.formatter,
    _ref$max = _ref.max,
    max = _ref$max === void 0 ? 100 : _ref$max,
    _ref$min = _ref.min,
    min = _ref$min === void 0 ? 0 : _ref$min,
    onChange = _ref.onChange,
    _ref$step = _ref.step,
    step = _ref$step === void 0 ? 1 : _ref$step,
    externalValue = _ref.value,
    ariaLabel = _ref['aria-label'],
    ariaLabelledBy = _ref['aria-labelledby'],
    ariaDescribedBy = _ref['aria-describedby'],
    rest = _objectWithoutProperties(_ref, _excluded);
  var trackRef = useRef(null);
  var _useControllableState = useControllableState(defaultValue !== undefined ? defaultValue : min, onChange, externalValue),
    _useControllableState2 = _slicedToArray(_useControllableState, 2),
    value = _useControllableState2[0],
    setValue = _useControllableState2[1];
  var _React$useState = React__default.useState(valueToPercent(value, max, min)),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    buttonPosition = _React$useState2[0],
    setButtonPosition = _React$useState2[1];
  var updateValue = function updateValue(newValue) {
    var roundedValue = roundToStep(newValue, max, min, step);
    if (roundedValue !== value) {
      setValue(roundedValue);
    }
  };
  var updateValueFromPoint = function updateValueFromPoint(point) {
    if (trackRef.current !== null) {
      var _trackRef$current$get = trackRef.current.getBoundingClientRect(),
        trackStart = _trackRef$current$get.left,
        trackWidth = _trackRef$current$get.width;
      var newPoint = Math.min(Math.max(0, point - trackStart), trackWidth);
      var newValue = pointToValue(newPoint, max, min, trackWidth);
      updateValue(newValue);
    }
  };

  // Update value when track is clicked
  var handleTrackClick = function handleTrackClick(e) {
    updateValueFromPoint(e.clientX);
  };
  var handleMouseMove = function handleMouseMove(e) {
    var currentMouseX = e.clientX;
    updateValueFromPoint(currentMouseX);
  };
  var handleKeyDown = function handleKeyDown(e) {
    var newValue;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        newValue = value + step;
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        newValue = value - step;
        break;
      case 'Home':
        newValue = min;
        break;
      case 'End':
        newValue = max;
        break;
      default:
        return;
    }
    e.preventDefault();
    updateValue(newValue);
  };
  var formattedValue = typeof formatter === 'function' ? formatter(value) :
  // TODO: Add automatic localization of number
  value.toLocaleString(undefined, {
    minimumFractionDigits: countDecimals(step),
    maximumFractionDigits: countDecimals(step)
  });

  // Update button position when value changes
  useSSRLayoutEffect(function () {
    setButtonPosition(valueToPercent(value, max, min));
  }, [max, min, value]);
  return /*#__PURE__*/React__default.createElement("div", _extends({}, rest, {
    className: classNames(styles.slider, className),
    ref: ref
  }), /*#__PURE__*/React__default.createElement("div", {
    className: styles.track,
    onClick: handleTrackClick,
    ref: trackRef
  }, /*#__PURE__*/React__default.createElement("span", {
    className: _fillInstanceProperty(styles),
    style: {
      left: -1,
      right: "".concat(100 - buttonPosition, "%")
    }
  }), /*#__PURE__*/React__default.createElement(SliderButton, {
    "aria-valuemin": min,
    "aria-valuemax": max,
    "aria-valuenow": value,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy,
    buttonPosition: buttonPosition,
    onMouseMove: handleMouseMove,
    onKeyDown: handleKeyDown,
    value: formattedValue
  })));
});

export { Slider, countDecimals, pointToValue, roundToStep, valueToPercent };
